import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import moment from 'moment';

import { isNilOrEmpty } from './ramdaUtils';

const DEFAULT_DATE_MASK = '99-aaa-9999';
const DATABASE_DATE_FORMAT = 'YYYY-MM-DD[T]HH:mm:ss.SSS';
const LOCALES_WITH_SPECIAL_SYMBOL = ['zh-cn', 'zh-mo', 'ja', 'ko'];

export const DATE_FORMAT = 'DD-MMM-YYYY';
export const DATE_FORMAT_WITH_TIMESTAMP = 'DD-MMM-YYYY HH:mm:ss';
export const DATE_FORMAT_WITH_MINUTES_TIMESTAMP = 'DD-MMM-YYYY HH:mm';
export const DATE_FORMAT_NUMERIC_WITH_TIMESTAMP = 'YYYY-MM-DD h:mm:ss';
export const EMPTY_INPUT_MASKS = [
  '__-___-____',
  '__-____-____',
  '__-__월-____',
  '__-__月-____',
  '__-_월-____',
  '__-_月-____',
  '__-____.-____',
  '__-___.-____',
  '__-_é__.-____',
  '__-_é_.-____',
  '__-__û_-____',
  '__-ú__-____',
  '__-ú_ř-____',
  '__-úč_-____',
  '__-ú_á-____',
  '__-úří-____',
  '__-_ä__-____',
  '__-_á__.-____',
  '__-_á_.-____',
  '__-á__.-____',
  '__-_ú_.-____',
  '__-_____.-____',
];

export const parseDateString = (input) => {
  const formats = [
    moment.ISO_8601,
    moment.RFC_2822,
    'YYYY-MM-DD',
    'M/D/YYYY h:mm:ss A',
    'M/D/YYYY',
    'LLLL',
  ];

  return moment(input, formats, true);
};

export const safeParseDateString = (input) => {
  if (isNilOrEmpty(input)) {
    return input;
  }

  return parseDateString(input);
};

export const formatMoment = (value) => {
  if (!moment.isMoment(value)) {
    return value;
  }

  try {
    let language = localStorage.getItem('culture').split('-')[0];

    if (language === 'zh') {
      language = localStorage.getItem('culture').toLowerCase();
    }

    if (language === 'zh-hant') {
      language = 'zh-mo';
    }

    require(`moment/locale/${language}`);

    value.locale(language);
  } catch (e) {
    value.locale();
  }

  return value.format(DATE_FORMAT);
};

export const dateToDatabaseDateString = (value) => {
  if (moment.isMoment(value)) {
    return value.format(DATABASE_DATE_FORMAT);
  }

  return moment(value).format(DATABASE_DATE_FORMAT);
};

export const formatDateString = (value) => {
  return formatMoment(parseDateString(value));
};

export const safeFormatDateString = (value) => {
  if (isNilOrEmpty(value)) {
    return value;
  }

  return formatDateString(value);
};

const parseValueToDate = (value) => {
  if (isNumber(value)) {
    return value;
  }

  return parseDateString(value);
};

export const formatStringToDateType = (value, isDateTimeFormatType) => {
  if (isNilOrEmpty(value)) {
    return null;
  }

  const dateFormat = isDateTimeFormatType ? DATE_FORMAT_WITH_TIMESTAMP : DATE_FORMAT;

  let date = parseValueToDate(value);
  if (!date.isValid()) {
    date = moment(value);
  }

  return date.format(dateFormat);
};

export const safeMomentDate = (value) => (value ? moment(value) : null);

export const processInputValue = (value, dateFormat = DATE_FORMAT) => {
  let inputValue = safeMomentDate(value);
  inputValue = isNil(inputValue)
    ? ''
    : inputValue.isValid()
    ? inputValue.format(dateFormat)
    : value;
  return inputValue;
};

export const setGlobalMomentLocale = () => {
  const culture = localStorage.getItem('culture');

  // Tries to load with country code. If it fails, just loads it with language.
  // Ex: pt-br.js vs pt.js or es-mx.js vs es.js
  if (culture && culture !== 'en-US') {
    try {
      let locale = culture.toLowerCase();
      // We're using Chinese (Macau) as a replacement for Chinese Traditional
      if (locale === 'zh-hant') {
        locale = 'zh-mo';
      }
      require(`moment/locale/${locale}`);
      moment.locale(locale);

      // we need this to display a proper name of week day in Calendar pop up
      if (locale === 'zh-cn' || locale === 'zh-mo') {
        moment.updateLocale(locale, { weekdaysShort: ['日', '一', '二', '三', '四', '五', '六'] });
      }
    } catch (err) {
      try {
        const [language] = culture.split('-');
        require(`moment/locale/${language}`);
        moment.locale(language);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(`🌎 Locale "${culture}" not found`);
      }
    }
  }
};

const getDefaultDateMask = () => {
  switch (moment.locale()) {
    case 'zh-cn':
    case 'zh-mo':
    case 'ja': {
      return '99-99月-9999';
    }

    case 'ko': {
      return '99-99월-9999';
    }

    default:
      return DEFAULT_DATE_MASK;
  }
};

export const getDateMask = (dateValue) => {
  const date =
    dateValue && moment(dateValue, DATE_FORMAT).isValid()
      ? moment(dateValue, DATE_FORMAT)
      : moment(dateValue).isValid()
      ? moment(dateValue)
      : moment();

  const isInvalidDate = !dateValue || (typeof dateValue === 'string' && dateValue.includes('_'));

  return isInvalidDate
    ? getDefaultDateMask()
    : date
        .format(getDateFormat(dateValue))
        .toString()
        .replace(/[0-9]/g, '9')
        .replace(/[A-Za-z]/g, 'a');
};

const getDefaultDateFormat = () => {
  switch (moment.locale()) {
    case 'zh-cn':
    case 'zh-mo':
    case 'ja': {
      return 'DD-MM月-YYYY';
    }

    case 'ko': {
      return 'DD-MM월-YYYY';
    }

    default:
      return DATE_FORMAT;
  }
};

export const getDateFormat = (dateValue) => {
  if (!dateValue || (typeof dateValue === 'string' && dateValue.includes('_'))) {
    return getDefaultDateFormat();
  }

  const date = moment(dateValue, getDefaultDateFormat());

  date.locale(moment.locale());

  switch (date.locale()) {
    case 'zh-mo':
    case 'zh-cn':
    case 'ja': {
      return date.month() < 9 ? 'DD-M月-YYYY' : 'DD-MM月-YYYY';
    }

    case 'ko': {
      return date.month() < 9 ? 'DD-M월-YYYY' : 'DD-MM월-YYYY';
    }

    default:
      return DATE_FORMAT;
  }
};

export const customCompareDates = (valueA, valueB, sortOrder) => {
  let result;

  // Check for empty values
  if (!valueA && valueB) {
    result = -1;
  } else if (!valueB && valueA) {
    result = 1;
  } else if (!valueA && !valueB) {
    result = 0;
  }

  if (!result) {
    const dateA = moment(valueA);
    const dateB = moment(valueB);

    // Check for invalid values
    if (!dateA.isValid() && dateB.isValid()) {
      result = -1;
    } else if (!dateB.isValid() && dateA.isValid()) {
      result = 1;
    } else if (!dateA.isValid() && !dateB.isValid()) {
      result = 0; // Finally compare the dates
    } else if (dateA.isBefore(dateB)) {
      result = -1;
    } else if (dateB.isBefore(dateA)) {
      result = 1;
    } else {
      result = 0;
    }
  }

  return sortOrder === 'asc' || result === 0 ? result : -result;
};

export const formatDatePickerValue = (selectedDate = {}) => {
  //when user select date in Calendar pop up, by clicking on "Today" button, selectedDate always has "en" locale
  const dateValue =
    selectedDate.locale() !== moment.locale()
      ? moment(selectedDate).locale(moment.locale())
      : selectedDate;

  return dateValue.format(getDateFormat(dateValue));
};

export const getDatePickerConfig = (storeValue, currentInputValue) => {
  const dateValueToUse = LOCALES_WITH_SPECIAL_SYMBOL.includes(moment.locale())
    ? storeValue
    : currentInputValue;

  const mask = getDateMask(dateValueToUse);
  const dateFormat = getDateFormat(dateValueToUse);
  //to avoid jumping between dates in calendar popup when user add/remove symbol one by one
  const dateValue =
    currentInputValue && !currentInputValue.includes('_') ? moment(dateValueToUse, dateFormat) : '';

  return { mask, dateFormat, dateValue };
};
